import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Hero.css';

const WORDS = [
  'Web Technologies',
  'Mobile Solutions',
  'Business Automation',
  'Digital Enterprises',
  'Cloud Services',
  'Digital Growth',
];

const COUNT    = 90;
const MAX_DIST = 130;
const MOUSE_R  = 160;

/* ── Particle network canvas ── */
function ParticleCanvas() {
  const canvasRef = useRef(null);
  const mouseRef  = useRef({ x: -9999, y: -9999 });
  const rafRef    = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap   = canvas.parentElement;
    const ctx    = canvas.getContext('2d');

    let W, H, particles = [];

    const rand = (a, b) => a + Math.random() * (b - a);

    const resize = () => {
      W = wrap.offsetWidth;
      H = wrap.offsetHeight;
      canvas.width  = W;
      canvas.height = H;
      particles = [];
      for (let i = 0; i < COUNT; i++) {
        particles.push({
          x:  rand(0, W),
          y:  rand(0, H),
          vx: rand(-0.08, 0.08),
          vy: rand(-0.06, 0.06),
          r:  rand(1.6, 3.2),
        });
      }
    };

    const onMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const onLeave = () => { mouseRef.current = { x: -9999, y: -9999 }; };

    wrap.addEventListener('mousemove', onMove);
    wrap.addEventListener('mouseleave', onLeave);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const { x: mx, y: my } = mouseRef.current;

      /* move + repel */
      for (const p of particles) {
        const dx = p.x - mx, dy = p.y - my;
        const d  = Math.sqrt(dx * dx + dy * dy);
        if (d < MOUSE_R && d > 0) {
          const force = (1 - d / MOUSE_R) * 0.6;
          p.vx += (dx / d) * force;
          p.vy += (dy / d) * force;
        }
        p.vx *= 0.992;
        p.vy *= 0.992;
        const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (spd > 0.35) { p.vx = p.vx / spd * 0.35; p.vy = p.vy / spd * 0.35; }
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
      }

      /* connections between particles */
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i], b = particles[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < MAX_DIST) {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(59,130,246,${(1 - dist / MAX_DIST) * 0.18})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }

        /* connections to mouse */
        const p = particles[i];
        const mdx = p.x - mx, mdy = p.y - my;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mdist < MOUSE_R) {
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(mx, my);
          ctx.strokeStyle = `rgba(243,49,22,${(1 - mdist / MOUSE_R) * 0.45})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }
      }

      /* draw dots */
      for (const p of particles) {
        const mdx = p.x - mx, mdy = p.y - my;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        const near  = Math.max(0, 1 - mdist / MOUSE_R);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r + near * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = near > 0.1
          ? `rgba(243,49,22,${0.35 + near * 0.45})`
          : 'rgba(59,130,246,0.22)';
        ctx.fill();
      }

      /* cursor dot */
      if (mx > 0) {
        ctx.beginPath();
        ctx.arc(mx, my, 4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(243,22,22,0.55)';
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      wrap.removeEventListener('mousemove', onMove);
      wrap.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="ssp-hero__canvas" aria-hidden="true" />;
}

/* ── Main Hero ── */
export default function Hero() {
  const navigate = useNavigate();
  const [wordIdx,   setWordIdx]   = useState(0);
  const [animState, setAnimState] = useState('visible');

  useEffect(() => {
    const hold = setTimeout(() => {
      setAnimState('exit');
      const swap = setTimeout(() => {
        setWordIdx((i) => (i + 1) % WORDS.length);
        setAnimState('enter');
        const settle = setTimeout(() => setAnimState('visible'), 420);
        return () => clearTimeout(settle);
      }, 320);
      return () => clearTimeout(swap);
    }, 2800);
    return () => clearTimeout(hold);
  }, [wordIdx]);

  return (
    <section className="ssp-hero">

      <ParticleCanvas />

      <div className="ssp-hero__blob ssp-hero__blob--1" aria-hidden="true" />
      <div className="ssp-hero__blob ssp-hero__blob--2" aria-hidden="true" />

      <div className="ssp-hero__inner">

        <h1 className="ssp-hero__h1">
          Innovative IT Solutions
          <br />
          <span className="ssp-hero__h1-row2">
            For&nbsp;
            <span className="ssp-hero__highlight-wrap">
              <span className="ssp-hero__highlight-bg" aria-hidden="true" />
              <span className={`ssp-hero__word ssp-hero__word--${animState}`}>
                {WORDS[wordIdx]}
              </span>
            </span>
          </span>
        </h1>

        <div className="hero-divider" />

        <p className="ssp-hero__sub">
          SkillSync Pro Pvt Ltd delivers scalable software, modern web &amp; mobile
          applications, and cutting-edge digital services that help businesses and
          students grow faster in the digital era.
        </p>

        <div className="ssp-hero__cta-row">
          <button className="ssp-hero__btn-primary" onClick={() => navigate('/services')}>
            Explore Services
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button className="ssp-hero__btn-ghost" onClick={() => navigate('/about')}>
            What We Do
          </button>
        </div>
      </div>

      <div className="ssp-hero__rule" aria-hidden="true" />
    </section>
  );
}